import { useEffect, useState, type ReactElement } from "react";
import type { PublicClientApplication } from "@azure/msal-browser";
import { Button, Stack, Text } from "@primitives/index";
import { AuthCard, BrandMark } from "@compositions/index";
import { color } from "@tokens/index";
import type { AppConfig } from "./auth/authConfig";

/**
 * ReauthGate — the "we lost the session, we are reconnecting" state.
 *
 * Visible between detecting an `InteractionRequiredError` and the browser
 * navigating to Microsoft via `acquireTokenRedirect`. Compositions are
 * unmounted while this gate renders, so users never see broken-data error
 * text. The redirect fires on mount; a 3s safety timeout reveals a manual
 * button in case the redirect is blocked.
 *
 * Shares `AuthCard` with `SignInGate` so the two read as one state machine.
 */
export interface ReauthGateProps {
    readonly instance: PublicClientApplication;
    readonly config: AppConfig;
    readonly appName: string;
}

const FALLBACK_REVEAL_MS = 3000;

export function ReauthGate({ instance, config, appName }: ReauthGateProps): ReactElement {
    const [showFallback, setShowFallback] = useState(false);

    useEffect(() => {
        void instance.acquireTokenRedirect({ scopes: [config.msal.apiScope] });
    }, [instance, config.msal.apiScope]);

    useEffect(() => {
        const id = window.setTimeout(() => {
            setShowFallback(true);
        }, FALLBACK_REVEAL_MS);
        return () => {
            window.clearTimeout(id);
        };
    }, []);

    const handleManualSignIn = (): void => {
        void instance.loginRedirect({ scopes: [config.msal.apiScope] });
    };

    return (
        <AuthCard role="status" aria-live="polite" aria-label="Refreshing your session">
            <Stack direction="column" gap={6} align="center">
                <BrandMark size={56} />

                <Stack direction="column" gap={2} align="center">
                    <Text as="h1" role="title" weight="bold">
                        {appName}
                    </Text>
                    <Text as="p" role="bodyLg" tone="muted">
                        Refreshing your session&hellip;
                    </Text>
                    <Text as="p" role="caption" tone="subtle">
                        Reconnecting to Microsoft to continue.
                    </Text>
                </Stack>

                {showFallback ? (
                    <Stack direction="column" gap={3} align="center">
                        <Text as="p" role="bodySm" tone="muted">
                            Taking longer than expected.
                        </Text>
                        <Button variant="primary" size="lg" onClick={handleManualSignIn}>
                            Sign in again
                        </Button>
                    </Stack>
                ) : (
                    <span
                        data-spinner=""
                        aria-hidden="true"
                        style={{ color: color.accent.primary }}
                    />
                )}
            </Stack>
        </AuthCard>
    );
}
